import React, { useEffect, useRef, useState } from "react";
import MUITable from "Components/UI/Tables/MUITable";
import styled from "@mui/material/styles/styled";
import PointStatusChip from "Components/UI/Tables/PointsStatusChip";

import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import {
  FormControlLabel,
  Radio,
  TableRow,
  TableCell,
  TableContainer,
  Collapse,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  TextField,
  Button,
} from "@mui/material";
import Paper from "Components/UI/Paper";
import { useEndCustomerPaid } from "Context/EndCustomerPaid";
import { Link } from "react-router-dom";
import { LoadingButton } from "@mui/lab";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { getAccountCharts } from "API/swesum";
import ExtendedControls from "./Components/ExtendedControls";
import ConnectionsTable from "./Components/ConnectionsTable";
import ConfirmCreditConnection from "./Components/ConfirmCreditConnection";
import SearchIcon from "@mui/icons-material/Search";
import { usePopupNotificationContext } from "Context/NotificationPopup";
import InvoiceInfoHeader from "./Components/InvoiceInfoHeader";

const StyledTable = styled(MUITable)(({ theme }) => ({
  backgroundColor: "#ffff",
  padding: "10px",
  border: "none",
  boxShadow: "none",
  ".MuiTable-root": {
    ".MuiTableCell-root": {
      "&:nth-of-type(1)": {
        paddingTop: "0",
        paddingBottom: "0",
      },
    },
  },
}));

const createTableRows = (paidInvoicesMatchArr) => {
  return paidInvoicesMatchArr
    .filter((invoice) => !invoice.Connected)
    .map((i) => {
      return [
        {
          accountId: i.AccountId,
          documentType: i.DocumentType,
          amount: i.Amount,
        },
        i.DocumentType,
        { invoiceNumber: i.ExternalInvoiceNumber, invoiceId: i.InvoiceId },
        // i.Substatus,
        i.OCR,
        i.OriginalAmount,
        i.InvoiceAmountLeft,
        i.ClientNumber,
        i.ClientName,
        i.EndCustomerNumber,
        i.Name,
        i.Points,
        i.Description,
        Number(i.Amount),
      ];
    });
};

export default function PaidInvoiceMatchTbl({
  selectedRowId,
  connectMatchesReq,
  handleRowSelection,
  handleOnRemove,
  matchArticle,
  matchAccount,
  matchCreditToInvoice,
  onSearch,
  loading,
}) {
  const { selectedInvoice } = useEndCustomerPaid();
  const [connectedInvoices, setConnectedInvoices] = useState([]);
  const [rows, setRows] = useState([]);
  const [showConnectedInvoice, setShowConnectedInvoice] = useState(false);
  const [showExtendedControls, setShowExtendedControls] = useState(false);
  const [accountCharts, setAccountCharts] = useState([]);
  const [adjustedInvoices, setAdjustedInvoices] = useState([]);

  const [selectedCreditId, setSelectedCreditId] = useState(null);
  const [
    selectedCreditConnectionInvoiceId,
    setSelectedCreditConnectionInvoiceId,
  ] = useState(null);

  const [openOIDMsg, setOpenOIDMsg] = useState(false);
  const [OIDMsg, setOIDMsg] = useState("");
  const [loadingOID, setLoadingOID] = useState(false);

  const iconButtonRef = useRef(null);
  const connectedInvoiceRef = useRef(null);

  const { openSuccess, openError } = usePopupNotificationContext();

  useEffect(() => {
    const asyncFetchAccountCharts = async () => {
      const res = await getAccountCharts();
      setAccountCharts(res);
    };
    asyncFetchAccountCharts();
  }, []);

  useEffect(() => {
    if (selectedInvoice?.InvoiceMatches.length > 0) {
      const tmpRows = createTableRows(
        selectedInvoice.InvoiceMatches.filter((invoice) => invoice.InvoiceId)
      );

      setRows(tmpRows);
      const tmpMatchedInvoices = selectedInvoice.InvoiceMatches.filter(
        (invoice) => invoice.Connected && invoice.InvoiceId
      );
      const adjustedInvoices = selectedInvoice.InvoiceMatches.filter(
        (invoice) => invoice.Connected && !invoice.InvoiceId
      );

      setAdjustedInvoices(adjustedInvoices);
      setShowConnectedInvoice(tmpMatchedInvoices.length > 0);
      if (tmpMatchedInvoices) {
        setConnectedInvoices(tmpMatchedInvoices);
      }
    }
  }, [selectedInvoice]);

  const columns = [
    {
      name: "Select",
      label: "Vald",
      options: {
        customBodyRender: (value) => {
          if (value.amount === 0) {
            return null;
          }

          if (value.documentType !== "Kredit") {
            return (
              <FormControlLabel
                control={
                  <Radio
                    checked={
                      selectedRowId !== null
                        ? Number(value.accountId) === Number(selectedRowId)
                        : false
                    }
                  />
                }
                label=""
              />
            );
          }

          // IF CREDIT
          return (
            <IconButton
              onClick={() =>
                setSelectedCreditId(
                  value.accountId === selectedCreditId ? null : value.accountId
                )
              }
              sx={{ marginLeft: "-10px" }}
            >
              {selectedCreditId === value.accountId ? (
                <RemoveIcon />
              ) : (
                <AddIcon />
              )}
            </IconButton>
          );
        },
      },
    },
    {
      name: "DocumentType",
      label: "Dokumenttyp",
    },
    {
      name: "InvoiceNumber",
      label: "Fakturanummer",
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => {
          return (
            <Link
              to={`/invoices/${value.invoiceId}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {value.invoiceNumber}
            </Link>
          );
        },
      },
    },
    /*{
      name: "Substatus",
      label: "Substatus",
      options: {
        customBodyRender: (substatus) => {
          return (
            <Chip
              label={getSubStatusLabelById(substatus)}
              color={getSubStatusColorById(substatus)}
              sx={{
                height: 24,
                width: "auto",
                minWidth: 80,
              }}
            />
          );
        },
      },
    },*/
    {
      name: "OCR",
      label: "OCR",
    },
    {
      name: "OriginalAmount",
      label: "Ursp belopp",
    },
    {
      name: "Amount",
      label: "Kvarst belopp",
    },
    {
      name: "ClientNumber",
      label: "Klientnummer",
    },
    {
      name: "ClientName",
      label: "Klientnamn",
    },
    {
      name: "EndCustomerNumber",
      label: "Kundnummer",
    },
    {
      name: "CustomerName",
      label: "Kundnamn",
    },
    {
      name: "Points",
      label: "Poäng",
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => {
          return <PointStatusChip point={value} />;
        },
      },
    },
    {
      name: "MatchDescription",
      label: "Beskrivning",
    },
    {
      name: "ConnectedAmount",
      label: "Kopplad summa",
    },
  ];

  const handleMatchArticle = async (articleId, msg) => {
    await matchArticle(selectedInvoice.Id, articleId, msg);
  };

  const handleMatchAccount = async (accountId, clientId = null) => {
    await matchAccount(selectedInvoice.Id, accountId, clientId);
  };

  const handleOIDClick = async () => {
    setLoadingOID(true);
    await handleMatchArticle(null, OIDMsg);
    setOpenOIDMsg(false);
    setLoadingOID(false);
    setShowExtendedControls(false);
  };

  const handleDropdownClick = async (accountId, clientId = null) => {
    await handleMatchAccount(accountId, clientId);
    setShowExtendedControls(false);
  };

  const handleConnectCredit = async (creditAccountId, invoiceAccountId) => {
    const creditInvoice = selectedInvoice.InvoiceMatches.find(
      (invoice) => invoice.AccountId === creditAccountId
    );
    const invoice = selectedInvoice.InvoiceMatches.find(
      (invoice) => invoice.AccountId === invoiceAccountId
    );
    const res = await matchCreditToInvoice(
      invoice.InvoiceId,
      creditInvoice.InvoiceId
    );

    if (res.status === 201) {
      openSuccess("OK!", 3000);
    } else {
      openError("Någonting gick fel", 3000);
    }
    setSelectedCreditConnectionInvoiceId(null);
    setSelectedCreditId(null);
  };

  const handleSearchIconClick = () => {
    onSearch();
  };

  const rowIsCredit = (rowIndex) => {
    return rows[rowIndex][0].documentType === "Kredit";
  };

  const handleRowClick = (rowIndex) => {
    if (!selectedCreditId) {
      if (!rowIsCredit(rowIndex)) {
        handleRowSelection(rows[rowIndex][0].accountId);
      } else if (rows[rowIndex][0].amount > 0) {
        setSelectedCreditId(rows[rowIndex][0].accountId);
      }
      return;
    }

    if (rows[rowIndex][0].accountId === selectedCreditId) {
      setSelectedCreditId(null);
      setSelectedCreditConnectionInvoiceId(null);
    } else if (!rowIsCredit(rowIndex)) {
      setSelectedCreditConnectionInvoiceId(
        selectedCreditConnectionInvoiceId === rows[rowIndex][0].accountId
          ? null
          : rows[rowIndex][0].accountId
      );
    }
  };

  const options = {
    customRowRender: (data, _, rowIndex) => {
      const isAmountZero = rows[rowIndex][0].amount === 0;

      return (
        <TableRow
          key={rowIndex}
          sx={{
            opacity: rowIsCredit(rowIndex) && selectedCreditId ? 0.5 : 1,
            background:
              selectedCreditConnectionInvoiceId === rows[rowIndex][0].invoiceId
                ? "rgba(25, 118, 210, 0.08)"
                : null,
          }}
          onClick={!isAmountZero ? () => handleRowClick(rowIndex) : undefined}
        >
          {data.map((cell, cellIndex) => {
            return <TableCell key={cellIndex}>{cell}</TableCell>;
          })}
        </TableRow>
      );
    },
    pagination: false,
    search: false,
    download: false,
    print: false,
    filter: false,
    columns: false,
    viewColumns: false,
    selectableRowsHeader: false,
    selectableRowsHideCheckboxes: true,
  };

  return (
    <>
      <Grid item container spacing={2}>
        <Grid item container>
          <Grid item md={2} />
          <Grid item md={8} flex={1}>
            <InvoiceInfoHeader
              invoice={selectedInvoice}
              onClickRef={(searchTerm) => {
                onSearch(searchTerm);
              }}
            />
          </Grid>
          <Grid
            md={2}
            item
            ref={connectedInvoiceRef}
            container
            justifyContent="flex-end"
            p="10px"
            direction="column"
            onClick={() => {
              if (selectedCreditConnectionInvoiceId || selectedCreditId) {
                setSelectedCreditConnectionInvoiceId(null);
                setSelectedCreditId(null);
              }
            }}
          >
            <Grid item container justifyContent="flex-end" pr="10px">
              <IconButton color="primary" onClick={handleSearchIconClick}>
                <SearchIcon />
              </IconButton>
              <IconButton
                ref={iconButtonRef}
                color="primary"
                onClick={() => setShowExtendedControls(!showExtendedControls)}
              >
                {showExtendedControls ? <RemoveIcon /> : <AddIcon />}
              </IconButton>
            </Grid>
            
            <ExtendedControls
              open={showExtendedControls}
              accountCharts={accountCharts}
              handleOIDClick={() => setOpenOIDMsg(true)}
              handleDropdownClick={handleDropdownClick}
              iconButtonRef={iconButtonRef}
            />
          </Grid>
        </Grid>

        {adjustedInvoices.length > 0 ? (
          <Grid item container justifyContent="center" direction="row" spacing={2} >
            {adjustedInvoices.map((invoice, index) => {
              return (
                <Grid item key={index}>
                  <Typography color="rgba(0, 0, 0, 0.6)" fontWeight="bold">
                    {invoice.Description}
                  </Typography>
                </Grid>
              );
            })}
            <Grid item sx={{ display: "flex", alignItems: "center" }}> 
              <LoadingButton
                variant="contained"
                color="error"
                size="small"
                loading={loading}
                onClick={() => handleOnRemove(selectedInvoice.InvoiceMatches[0])}
              >
                Avkoppla
              </LoadingButton>
            </Grid>
            
          </Grid>
        ) : null}
      </Grid>
      <Collapse in={showConnectedInvoice}>
        <Grid item container justifyContent="center">
          <TableContainer component={Paper} sx={{ margin: "10px" }}>
            <Grid item md={8} sm={12}>
              <Typography textAlign="left" variant="h6">
                Kopplingar
              </Typography>
            </Grid>
            <ConnectionsTable
              connectedInvoices={connectedInvoices}
              handleOnRemove={handleOnRemove}
              loading={loading}
            />
          </TableContainer>
        </Grid>
      </Collapse>

      <ConfirmCreditConnection
        anchorRef={connectedInvoiceRef}
        open={!!selectedCreditConnectionInvoiceId}
        onConfirm={() =>
          handleConnectCredit(
            selectedCreditId,
            selectedCreditConnectionInvoiceId
          )
        }
        onClose={() => {
          setSelectedCreditConnectionInvoiceId(null);
          setSelectedCreditId(null);
        }}
        credit={selectedInvoice?.InvoiceMatches.find(
          (inv) => inv.AccountId === selectedCreditId
        )}
        invoice={selectedInvoice?.InvoiceMatches.find(
          (inv) => inv.AccountId === selectedCreditConnectionInvoiceId
        )}
      />

      <Collapse in={rows.length > 0}>
        <div
          style={{ marginTop: "20px", maxHeight: "300px", overflow: "auto" }}
        >
          <Typography textAlign="left" marginLeft="25px" variant="h6">
            Kopplingsförslag
          </Typography>
          <StyledTable options={options} data={rows} columns={columns} />
        </div>
        <Grid
          item
          xs={12}
          md={12}
          sx={{
            display: "flex",
            justifyContent: "center",

            marginBottom: "10px",
          }}
        >
          <LoadingButton
            disabled={!selectedInvoice || selectedRowId === null}
            onClick={() => connectMatchesReq()}
            variant="contained"
            loading={loading && !(!selectedInvoice || selectedRowId === null)}
          >
            Koppla
          </LoadingButton>
        </Grid>
      </Collapse>

      <Dialog open={openOIDMsg} onClose={() => setOpenOIDMsg(false)}>
        <DialogTitle>{"Lägg till kommentar"}</DialogTitle>
        <DialogContent>
          <DialogContentText>Kommentar</DialogContentText>
          <TextField
            fullWidth
            value={OIDMsg}
            multiline
            rows={4}
            onChange={(e) => setOIDMsg(e.target.value)}
            sx={{ minWidth: "500px" }}
          />
          <div style={{ display: "flex", gap: "15px", margin: "10px 0px" }}>
            <LoadingButton
              loading={loadingOID}
              variant="contained"
              onClick={() => handleOIDClick()}
            >
              OK
            </LoadingButton>
            <Button onClick={() => setOpenOIDMsg(false)}>Avbryt</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
